import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/tokens.css';
import './styles/reset.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
// ANL-PR3: the web-vitals → perf-telemetry bridge. Maps each Core Web Vital to
// its PERF_METRICS name and beacons it to /telemetry/perf (best-effort, silent,
// honors the disabled-after-404/403 latch; skips FCP + guests).
import { handleWebVital } from './telemetry/reportPerf';
import { registerServiceWorker } from './utils/registerServiceWorker';
import { installAxiosRefreshInterceptor, clearTokens } from './setupAxiosInterceptor';
import { installAxiosAuthHeaderInterceptor } from './setupAxiosAuthHeader';

// Install the request-side Authorization-header interceptor BEFORE any
// component mounts (cold-boot auth-race fix, 2026-07-12). It reads the
// bearer token from localStorage on EVERY outgoing request, so the very
// first request any component fires — regardless of which mount effect
// runs first — carries the header. See setupAxiosAuthHeader.js for the
// full bug writeup (React commits child effects before parent effects,
// so the OLD approach of setting the header from AuthenticatedApp's
// `useEffect` could lose that race on a cold boot).
installAxiosAuthHeaderInterceptor();

// Install the silent refresh-token interceptor before any axios
// request fires (POST-AIQ-042, Phase 2). Catches 401s, calls
// /auth/refresh once-per-burst (single-flight), retries the original
// request with the new token. On reuse-detected (family revoked),
// clears tokens + the App-level Login-screen redirect handles the
// rest on next render. See setupAxiosInterceptor.js for the full
// engineering rationale.
installAxiosRefreshInterceptor({
  onReuseDetected: () => {
    // Tokens already cleared inside the interceptor; clear `user`
    // here so the next render of <App> bails to <Login>. We don't
    // hard-redirect via window.location — that would discard React
    // state mid-request; the App's auth-check effect handles
    // navigation when token+user go null.
    clearTokens();
    localStorage.removeItem('user');
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// ANL-PR3: feed each Core Web Vital to the perf-telemetry bridge. web-vitals
// reports each metric once per page load; handleWebVital maps it to its
// PERF_METRICS name and POSTs a one-sample /telemetry/perf batch. It is a no-op
// for guests (no token) and after the disabled latch trips, so passing it
// unconditionally here is safe — the gating lives in the beacon, not the wiring.
reportWebVitals(handleWebVital);

// Register the runtime-caching service worker after the page has
// loaded, so it doesn't compete with the critical-path JS/CSS
// fetches. Skipped in dev / localhost so live-reload works (perf
// #5/15, 2026-04-25). On a deploy, the SW's `updatefound` event
// fires; we let the next user navigation pick up the new bundle
// rather than auto-reloading mid-question.
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    registerServiceWorker().catch(() => { /* never block boot on SW errors */ });
  });
}
